import { demoFundTickers } from '../constants'
import { checkToken, handleError, wiseFundsRateLimiter } from '../utils'
import { httpClient } from '../http-client'

/**
 * Get ETF data
 * @customfunction
 * @helpurl https://blog.wisesheets.io/the-ultimate-guide-to-using-wisesheets/
 * @param {string[][]} symbols ETF symbol.
 * @param {string[][]} parameters Data point(s) to retrieve.
 * @returns {any[][]} Data or some error.
 */
// @ts-ignore
async function WISEFUNDS(symbols, parameters) {
    if (symbols.flat().length > 5001) {
        return [['Please select less than 5,000 tickers to get data.']]
    }
    // html encoding for ^ and filter out blank values, maintaining original structure
    const filteredSymbols = symbols
        .map((row: string[]) =>
            row
                .map((symbol: string) =>
                    symbol.toUpperCase().replace('^', '%5E').trim()
                )
                .filter((symbol: string) => symbol !== '')
        )
        .filter((row: string[]) => row.length > 0)

    const symbolsToCheck = filteredSymbols.flat()

    const { error, subscriptionType } = checkToken(
        symbolsToCheck,
        demoFundTickers
    )

    if (error) {
        return error
    }

    try {
        // Batch requests in groups of 1000 tickers using filtered symbols
        const BATCH_SIZE = 1000

        // Calculate weight (number of batches) for rate limiting
        const weight = Math.ceil(symbolsToCheck.length / BATCH_SIZE)

        const rateLimitError = await wiseFundsRateLimiter.checkAndApplyLimit(
            subscriptionType,
            weight
        )
        if (rateLimitError) {
            return [[rateLimitError]]
        }

        // If symbols length is within batch size, make single request
        if (symbolsToCheck.length <= BATCH_SIZE) {
            const data = await httpClient.post(
                '/api/private/functions/wise-funds',
                {
                    symbols: filteredSymbols,
                    parameters,
                    platform: 'EXCEL',
                }
            )
            return data.data
        }

        // Create batches from filteredSymbols maintaining 2D array structure
        const batches: string[][][] = []
        let currentBatch: string[][] = []
        let currentCount = 0

        for (const row of filteredSymbols) {
            for (const symbol of row) {
                if (currentCount >= BATCH_SIZE) {
                    batches.push(currentBatch)
                    currentBatch = []
                    currentCount = 0
                }
                currentBatch.push([symbol])
                currentCount++
            }
        }

        // Push the last batch if it has items
        if (currentBatch.length > 0) {
            batches.push(currentBatch)
        }

        // Process all batches in parallel
        const batchPromises = batches.map((batch) =>
            httpClient.post('/api/private/functions/wise-funds', {
                symbols: batch,
                parameters,
                platform: 'EXCEL',
            })
        )

        // Wait for all requests to complete (order is maintained)
        const batchResponses = await Promise.all(batchPromises)

        // Combine results from all batches
        const results: any[][] = []
        for (const response of batchResponses) {
            // Each batch returns a 2D array, concatenate rows
            for (const row of response.data) {
                results.push(row)
            }
        }

        return results
    } catch (e) {
        return handleError(e)
    }
}

// Export for FMP wrapper functions
export { WISEFUNDS }

CustomFunctions.associate("WISEFUNDS", WISEFUNDS);